import type { TableProps } from '@innovationdepartment/proxima-ui';
import { useFeatureFlag } from 'hooks';
import { useState } from 'react';
import type {
  AudienceTableCellRendererProps,
  SeedAudienceSchema,
  SeedAudiencesTableProps,
  SeedAudienceTableColumnHeader,
  SeedAudienceTableRowExtraFunctions,
  SeedAudienceTableRowProps
} from 'types/audiences';
import LookalikeAudiencesTable from '../LookalikeAudiencesTable';
import SeedAudiencesTableEmptyContent from './SeedAudiencesTable.EmptyContent';
import SeedAudiencesTableView from './SeedAudiencesTable.View';
import seedAudiencesRowRenderer from './seedAudiencesRowRenderer';

type RowRendererProps = AudienceTableCellRendererProps<SeedAudienceTableRowProps> &
  SeedAudienceTableRowExtraFunctions;

const SeedAudiencesTable = (props: SeedAudiencesTableProps) => {
  const [hoverId, setHoverId] = useState<string | null>(null);
  const {
    seedAudiences,
    onShowRenameSeedAudienceModal,
    onShowRenameLookalikeAudienceModal,
    onShowDeleteLookalikeAudienceModal,
    onShowAudienceSettingsModal,
    onShowDeleteSeedAudienceModal,
    onShowCreateLookalikeAudienceModal
  } = props;
  const { tiktokIntegration } = useFeatureFlag();

  const headerOrder = [
    'seedAudience',
    'platform',
    'adAccount',
    'displayStatus',
    'dateCreated',
    'actions'
  ] satisfies SeedAudienceTableColumnHeader[];

  if (!tiktokIntegration) headerOrder.splice(1, 1);

  const config: TableProps<SeedAudienceSchema>['config'] = {
    columns: {
      order: headerOrder,
      seedAudience: {
        label: 'Custom audience'
      },
      displayStatus: {
        label: 'Status',
        width: 200
      },
      platform: {
        label: 'Platform',
        width: 160
      },
      adAccount: {
        label: 'Ad Account',
        width: 200
      },
      dateCreated: {
        label: 'Date created',
        align: 'right',
        width: 140
      },
      actions: {
        hideLabel: true,
        width: 50
      }
    },
    cellRenderer(row) {
      return seedAudiencesRowRenderer({
        ...row,
        onShowRenameSeedAudienceModal,
        onShowAudienceSettingsModal,
        onShowDeleteSeedAudienceModal,
        onShowCreateLookalikeAudienceModal
      } as RowRendererProps);
    },
    // eslint-disable-next-line
    emptyStateRenderer: () => <SeedAudiencesTableEmptyContent />,
    onRowMouseEnter: ({ id }) => {
      setHoverId(id);
    },
    onRowMouseLeave: () => {
      setHoverId(null);
    },
    formatter(row: SeedAudienceSchema) {
      return {
        ...row,
        isHovered: row.id === hoverId
      };
    },
    // eslint-disable-next-line
    onRowExpand: ({ row }) => (
      // TODO(Jenky): Get ad account id from integrationAudiences array (match with externalId in lookalikeAudiences schema)
      <LookalikeAudiencesTable
        lookalikeAudiences={row?.lookalikeAudiences ?? []}
        onShowRenameLookalikeAudienceModal={onShowRenameLookalikeAudienceModal}
        onShowDeleteLookalikeAudienceModal={onShowDeleteLookalikeAudienceModal}
      />
    )
  };

  return <SeedAudiencesTableView seedAudiences={seedAudiences} config={config} />;
};

export default SeedAudiencesTable;
